import {
  loadMarketIntelligenceMock,
  MARKET_INTELLIGENCE_USE_MOCK_DATA,
  MarketplaceIntelligenceBaseApiClient
} from '../marketplace-intelligence/client';
import type {
  FeatureImportance,
  ModelMetrics,
  PersonaInput,
  PricePrediction,
  ShapExplanation,
  WhatIfResult
} from '@interfaces/Prediction.interface';

export class ApiPredictions extends MarketplaceIntelligenceBaseApiClient {
  async getModelMetrics(studyId: string): Promise<ModelMetrics[]> {
    if (MARKET_INTELLIGENCE_USE_MOCK_DATA) {
      const mock = await loadMarketIntelligenceMock();
      return mock.getMockModelMetrics(studyId);
    }
    return this.get<ModelMetrics[]>('/api/v1/predictions/metrics', {
      params: { study_id: studyId }
    });
  }

  metrics(studyId: string): Promise<ModelMetrics[]> {
    return this.getModelMetrics(studyId);
  }

  async getFeatureImportance(
    studyId: string,
    brand: string
  ): Promise<FeatureImportance[]> {
    if (MARKET_INTELLIGENCE_USE_MOCK_DATA) {
      const mock = await loadMarketIntelligenceMock();
      return mock.getMockFeatureImportance(studyId, brand);
    }
    return this.get<FeatureImportance[]>('/api/v1/predictions/importance', {
      params: { study_id: studyId, brand }
    });
  }

  importance(studyId: string, brand: string): Promise<FeatureImportance[]> {
    return this.getFeatureImportance(studyId, brand);
  }

  async predict(
    studyId: string,
    brand: string,
    persona: PersonaInput
  ): Promise<PricePrediction> {
    if (MARKET_INTELLIGENCE_USE_MOCK_DATA) {
      const mock = await loadMarketIntelligenceMock();
      return mock.getMockPrediction(brand, persona);
    }
    return this.post<PricePrediction>('/api/v1/predictions/predict', {
      study_id: studyId,
      brand,
      persona
    });
  }

  async explain(
    studyId: string,
    brand: string,
    persona: PersonaInput
  ): Promise<ShapExplanation> {
    if (MARKET_INTELLIGENCE_USE_MOCK_DATA) {
      const mock = await loadMarketIntelligenceMock();
      return mock.getMockExplanation(brand, persona);
    }
    return this.post<ShapExplanation>('/api/v1/predictions/explain', {
      study_id: studyId,
      brand,
      persona
    });
  }

  explainPrediction(
    studyId: string,
    persona: PersonaInput,
    brand: string,
    _persona_id = 'simulator'
  ): Promise<ShapExplanation> {
    return this.explain(studyId, brand, persona);
  }

  async whatIf(
    studyId: string,
    brandOrParams:
      | string
      | {
          base_persona?: PersonaInput;
          persona?: PersonaInput;
          modifications?: Record<string, unknown>;
          changes?: Record<string, unknown>;
          brand: string;
          persona_id?: string;
        },
    personaArg?: PersonaInput,
    changesArg?: PersonaInput
  ): Promise<WhatIfResult> {
    const brand =
      typeof brandOrParams === 'string' ? brandOrParams : brandOrParams.brand;
    const persona =
      typeof brandOrParams === 'string'
        ? personaArg ?? {}
        : brandOrParams.persona ?? brandOrParams.base_persona ?? {};
    const changes =
      typeof brandOrParams === 'string'
        ? changesArg ?? {}
        : brandOrParams.changes ?? brandOrParams.modifications ?? {};

    if (MARKET_INTELLIGENCE_USE_MOCK_DATA) {
      const mock = await loadMarketIntelligenceMock();
      return mock.getMockWhatIf(brand, persona, changes);
    }
    return this.post<WhatIfResult>('/api/v1/predictions/what-if', {
      study_id: studyId,
      brand,
      persona,
      changes
    });
  }
}

export { predictionsQueryKeys } from './query-keys';
export * from './use-predictions-queries';
export * from './use-predictions-mutations';
export { ApiPredictions as PredictionsApi };
